import {JanusInstance, JanusStatus, PluginStatus} from "../../../constants/Janus";
import Janus from "../../../utils/Janus/janus";
import {useCallback, useReducer} from "react";
import {audioBridgeReducer, initialAudioBridgeState} from "../../../store/reducers/AudioBridge";
import {AudioBridgeEvent} from "../../../constants/Janus/AudioBridge";
import {attachAudioBridge, joinAudioRoom, publishAudio} from "../../../utils/Janus/AudioBridge";

interface useAudioBridgeProps {
    janusInstance: JanusInstance
    janusStatus: JanusStatus
    JanusModule?: any
    onRemoteStream: (Janus: any, stream: any) => void
}

export const useAudioBridge = ({
                                janusInstance,
                                janusStatus,
                                JanusModule = Janus,
                                   onRemoteStream
                            }: useAudioBridgeProps) => {
    const [audioBridge, dispatch] = useReducer(audioBridgeReducer, initialAudioBridgeState);

    const joinRoom = useCallback((room: number, display: string, muted: boolean = false) => {
        joinAudioRoom(audioBridge.mixerPlugin ,room, display, muted)
    }, [audioBridge.mixerPlugin])

    const publishAudioBridge = useCallback((muted: boolean = false) => {
        publishAudio(JanusModule, audioBridge.mixerPlugin, muted,
            (event: any, data: any) => {
                switch (event) {
                    case 'success': {
                        dispatch({type: AudioBridgeEvent.SET, payload: {key: "muted", value: muted}});
                        return {event, data}
                    }
                    case 'error': {
                        return {event, data}
                    }
                }
            })

    }, [JanusModule, audioBridge.mixerPlugin])

    const toggleAudio = useCallback(() => {
        if (!audioBridge.mixerPlugin) return;
        audioBridge.mixerPlugin.send({ message: { request: "configure", muted: !audioBridge.muted }});
        dispatch({type: AudioBridgeEvent.SET, payload: {key: "muted", value: !audioBridge.muted}});
    }, [JanusModule, audioBridge.mixerPlugin, audioBridge.muted])

    const attachAudioBridgePlugin = useCallback(()  => {
        dispatch({type: AudioBridgeEvent.SET, payload: {key: "pluginStatus", value: PluginStatus.Attaching}});
        const opaqueId: string = "audiobridge-" + JanusModule.randomString(12);

        attachAudioBridge(JanusModule, janusInstance, opaqueId,
            (mixer: any, eventType: AudioBridgeEvent, payload: any) => {
                dispatch({type: AudioBridgeEvent.SET, payload: {key: "mixerPlugin", value: mixer}});
                switch (eventType) {
                    case AudioBridgeEvent.SUCCESS: {
                        dispatch({type: AudioBridgeEvent.SUCCESS});
                        break;
                    }
                    case AudioBridgeEvent.ERROR: {
                        dispatch({ type: AudioBridgeEvent.ERROR, payload });
                        break;
                    }
                    case AudioBridgeEvent.CONSENT_DIALOG: {
                        dispatch({ type: AudioBridgeEvent.CONSENT_DIALOG, payload });
                        break;
                    }
                    case AudioBridgeEvent.ICE_STATE: {
                        dispatch({ type: AudioBridgeEvent.ICE_STATE, payload });
                        break;
                    }
                    case AudioBridgeEvent.MEDIA_STATE: {
                        dispatch({ type: AudioBridgeEvent.MEDIA_STATE, payload });
                        break;
                    }
                    case AudioBridgeEvent.WEBRTC_STATE: {
                        dispatch({ type: AudioBridgeEvent.WEBRTC_STATE, payload });
                        break;
                    }
                    case AudioBridgeEvent.JOINED: {
                        dispatch({ type: AudioBridgeEvent.JOINED, payload });
                        break;
                    }
                    case AudioBridgeEvent.TALKING: {
                        dispatch({ type: AudioBridgeEvent.TALKING, payload });
                        break;
                    }
                    case AudioBridgeEvent.STOPPED_TALKING: {
                        dispatch({ type: AudioBridgeEvent.STOPPED_TALKING, payload });
                        break;
                    }
                    case AudioBridgeEvent.DESTROYED: {
                        dispatch({ type: AudioBridgeEvent.DESTROYED });
                        break;
                    }
                    case AudioBridgeEvent.PARTICIPANTS: {
                        dispatch({ type: AudioBridgeEvent.PARTICIPANTS, payload });
                        break;
                    }
                    case AudioBridgeEvent.ERROR_EVENT: {
                        dispatch({ type: AudioBridgeEvent.ERROR_EVENT, payload });
                        break;
                    }
                    case AudioBridgeEvent.LEAVING: {
                        dispatch({ type: AudioBridgeEvent.LEAVING, payload });
                        break;
                    }
                    case AudioBridgeEvent.ON_REMOTE_STREAM: {
                        onRemoteStream(JanusModule, payload);
                        dispatch({ type: AudioBridgeEvent.ON_REMOTE_STREAM, payload });
                        break;
                    }
                    case AudioBridgeEvent.ON_CLEAN_UP: {
                        dispatch({ type: AudioBridgeEvent.ON_CLEAN_UP });
                        break;
                    }
                }
            })

    }, [JanusModule, janusInstance])

    return {
        ...audioBridge,
        joinRoom,
        attachAudioBridgePlugin,
        publishAudioBridge,
        toggleAudio
    }
}